import { AHText, Box, Circle, useScaleFactor, useTheme } from "arkh";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState } from "react";
import { StyleSheet } from "react-native";
import { graphql, useFragment } from "react-relay";
import { Tile_kpiTile$key } from "./__generated__/Tile_kpiTile.graphql";
import { Tile_kpiTileGraphBar$key } from "./__generated__/Tile_kpiTileGraphBar.graphql";
import { Tile_kpiTileGraphSlider$key } from "./__generated__/Tile_kpiTileGraphSlider.graphql";
import { Tile_kpiTileGraphRegions$key } from "./__generated__/Tile_kpiTileGraphRegions.graphql";
import { Tile_kpiTileGraphGauge$key } from "./__generated__/Tile_kpiTileGraphGauge.graphql";
import Svg, { Line } from "react-native-svg";
export const Tile = ({ tileKey }: { tileKey: Tile_kpiTile$key }) => {
  const tile = useFragment(
    graphql`
      fragment Tile_kpiTile on WidgetKPITile {
        title
        subtitle
        value
        unit
        trends {
          change
          label
          compareValue
          type
        }
        graph {
          label
          ... on WidgetKPITileGraphBar {
            __typename
            ...Tile_kpiTileGraphBar
          }
          ... on WidgetKPITileGraphSlider {
            __typename
            ...Tile_kpiTileGraphSlider
          }
          ... on WidgetKPITileGraphRegions {
            __typename
            ...Tile_kpiTileGraphRegions
          }
          ... on WidgetKPITileGraphGauge {
            __typename
            ...Tile_kpiTileGraphGauge
          }
        }
      }
    `,
    tileKey
  );

  return (
    <Box
      color="surface-primary"
      style={{ width: "100%", height: "100%", borderWidth: 1 }}
      borderColor="border-primary"
      cornerRadius="medium"
      space="small"
    >
      <AHText type="subtitle">{tile.title}</AHText>
      {tile.subtitle && <AHText type="detail">{tile.subtitle}</AHText>}

      <Box style={{ flex: 1, justifyContent: "center" }}>
        <Box
          style={{
            flexDirection: "row",
            alignItems: "baseline",
            flexWrap: "wrap",
            overflow: "hidden",
          }}
          spaceBottom="small"
        >
          <AHText
            type="title-big"
            style={{ maxWidth: "100%" }}
            numberOfLines={1}
            adjustsFontSizeToFit
          >
            {tile.value}
          </AHText>
          {tile.unit && (
            <AHText type="info-small" spaceLeft="xsmall">
              {tile.unit}
            </AHText>
          )}
        </Box>
        {tile.trends?.map((trend, i) => (
          <Box
            key={i + ""}
            spaceBottom="small"
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Box spaceRight="xsmall">
              <Box
                cornerRadius="full"
                spaceHorizontal="small"
                spaceVertical="xsmall"
                style={{ position: "relative", overflow: "hidden" }}
              >
                <AHText type="subtitle" color={trend.type} darkenColor={0.3}>
                  {trend.change}
                </AHText>
                <Box
                  style={[StyleSheet.absoluteFill, { opacity: 0.1 }]}
                  color={trend.type}
                />
              </Box>
            </Box>
            <AHText spaceLeft="xsmall">
              {trend.label}
              {trend.compareValue ? ` (${trend.compareValue})` : null}
            </AHText>
          </Box>
        ))}
      </Box>

      {tile.graph && (
        <Box>
          {tile.graph.label && (
            <AHText type="detail" space="xsmall">
              {tile.graph.label}
            </AHText>
          )}
          <Box cornerRadius="full" space="xsmall" color="surface-background">
            {tile.graph.__typename === "WidgetKPITileGraphBar" ? (
              <BarGraph graph={tile.graph} />
            ) : tile.graph.__typename === "WidgetKPITileGraphSlider" ? (
              <SliderGraph graph={tile.graph} />
            ) : tile.graph.__typename === "WidgetKPITileGraphRegions" ? (
              <RegionsGraph graph={tile.graph} />
            ) : tile.graph.__typename === "WidgetKPITileGraphGauge" ? (
              <GaugeGraph graph={tile.graph} />
            ) : null}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const BarGraph = ({ graph }: { graph: Tile_kpiTileGraphBar$key }) => {
  const { parts } = useFragment(
    graphql`
      fragment Tile_kpiTileGraphBar on WidgetKPITileGraphBar {
        parts
      }
    `,
    graph
  );

  const sf = useScaleFactor();

  return (
    <Box
      style={{
        width: `${parts * 100}%`,
        minWidth: sf * 6,
        height: 12 * sf,
      }}
      cornerRadius="full"
      color="surface-cta"
    />
  );
};

const SliderGraph = ({ graph }: { graph: Tile_kpiTileGraphSlider$key }) => {
  const { parts } = useFragment(
    graphql`
      fragment Tile_kpiTileGraphSlider on WidgetKPITileGraphSlider {
        parts
      }
    `,
    graph
  );

  const [width, setWidth] = useState(12);

  const sf = useScaleFactor();
  const theme = useTheme();

  return (
    <Box
      style={{
        width: "100%",
        height: 12 * sf,
        overflow: "hidden",
        position: "relative",
      }}
      cornerRadius="full"
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
    >
      <LinearGradient
        colors={[theme.semiNegative, theme.semiNegative]}
        style={StyleSheet.absoluteFill}
      />
      <LinearGradient
        colors={[theme.negative, `${theme.negative}00`]}
        locations={[0, 0.4944]}
        style={StyleSheet.absoluteFill}
        start={{ x: 1, y: 0 }}
        end={{ x: 0, y: 0 }}
      />
      <LinearGradient
        colors={[theme.positive, `${theme.positive}00`]}
        locations={[0, 0.734]}
        style={StyleSheet.absoluteFill}
        end={{ x: 1, y: 0 }}
      />
      <Circle
        size="large"
        style={{
          width: sf * 12,
          height: sf * 12,
          position: "absolute",
          left: (width - 12 * sf) * parts,
        }}
        color="surface-dark"
      />
    </Box>
  );
};

const RegionsGraph = ({ graph }: { graph: Tile_kpiTileGraphRegions$key }) => {
  const { negative, positive } = useFragment(
    graphql`
      fragment Tile_kpiTileGraphRegions on WidgetKPITileGraphRegions {
        negative
        positive
      }
    `,
    graph
  );

  const sf = useScaleFactor();

  return (
    <Box
      style={{ flexDirection: "row", height: sf * 12, overflow: "hidden" }}
      cornerRadius="full"
    >
      <Box style={{ width: `${negative * 100}%` }} color="status-negative" />
      <Box style={{ height: sf * 12, flex: 1 }} color="status-semiNegative" />
      <Box style={{ width: `${positive * 100}%` }} color="status-positive" />
    </Box>
  );
};

function GaugeGraph(props: { graph: Tile_kpiTileGraphGauge$key }) {
  const sf = useScaleFactor();

  const data = useFragment(
    graphql`
      fragment Tile_kpiTileGraphGauge on WidgetKPITileGraphGauge {
        gaugeParts {
          color
          value
          mode
        }
        indicators {
          type
          value
        }
      }
    `,
    props.graph
  );

  const totalGaugeValue = data.gaugeParts.reduce(
    (acc, part) => acc + part.value,
    0
  );

  const height = sf * 12;

  const primaryIndicatorHeight = height + sf * 8;
  const secondaryIndicatorHeight = height + sf * 4;
  return (
    <Box>
      <Box
        style={{ flexDirection: "row", height, overflow: "hidden" }}
        cornerRadius="full"
      >
        {data.gaugeParts.map((part, i) => (
          <Box
            key={i + ""}
            style={{
              width: `${(part.value / totalGaugeValue) * 100}%`,
              opacity: part.mode === "lowlight" ? 0.4 : 1,
            }}
            color={part.color}
          />
        ))}
      </Box>
      {data.indicators.map((indicator, i) =>
        indicator.type === "primary" ? (
          <Box
            key={i + ""}
            style={{
              left: `${(indicator.value / totalGaugeValue) * 100}%`,
              width: 4,
              marginLeft: -2,
              height: primaryIndicatorHeight,
              top: -(primaryIndicatorHeight - height) / 2,
              position: "absolute",
            }}
            color="text-primary"
            cornerRadius="full"
          />
        ) : (
          <Svg
            key={i + ""}
            height={secondaryIndicatorHeight}
            width={2}
            style={{
              left: `${(indicator.value / totalGaugeValue) * 100}%`,
              width: 2,
              marginLeft: -1,
              height: secondaryIndicatorHeight,
              top: -(secondaryIndicatorHeight - height) / 2,
              position: "absolute",
            }}
          >
            <Line
              x1={1}
              y1={0}
              x2={1}
              y2={secondaryIndicatorHeight}
              stroke="#000"
              opacity={0.5}
              strokeWidth={2}
              strokeDasharray={[3, 2]}
            />
          </Svg>
        )
      )}
    </Box>
  );
}
